import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Upload, Loader2, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable image uploader for admin.
 * value = "" | "https://..." | "/api/files/..."
 * onChange(newUrl) called after successful upload or on remove.
 */
export function ImageUploader({ value, onChange, label = "Immagine", testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const preview = value
    ? (value.startsWith("http") ? value : `${BACKEND}${value}`)
    : "";

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File troppo grande (max 8MB)");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Immagine caricata");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">{label}</span>
      <div className="mt-2 flex gap-3 items-start">
        <div
          className={`w-28 h-28 rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden shrink-0 ${
            preview ? "border-transparent bg-[#F2EBE1]" : "border-[#E5DCD0] bg-[#FBF9F6]"
          }`}
        >
          {preview ? (
            <img src={preview} alt="" className="w-full h-full object-cover"/>
          ) : (
            <ImagePlus size={24} className="text-[#7A7369]"/>
          )}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid={testid}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white px-4 py-2 text-xs font-bold hover:bg-[#3D5844] disabled:opacity-60 transition-all"
            data-testid={testid ? `${testid}-btn` : undefined}
          >
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
            {value ? "Sostituisci" : "Carica file"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C8795A] hover:underline self-start"
            >
              <X size={12}/> Rimuovi
            </button>
          )}
          <span className="text-[11px] text-[#7A7369]">
            JPEG, PNG, WEBP · max 8MB. In alternativa incolla un URL nel campo sotto.
          </span>
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Oppure incolla URL esterno https://..."
            className="w-full h-9 rounded-lg border border-[#E5DCD0] bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#4A6B53]"
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Gallery uploader — array of URLs
 */
export function GalleryUploader({ value = [], onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const added = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith("image/")) continue;
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: troppo grande`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        added.push(data.url);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    }
    if (added.length) {
      onChange([...(value || []), ...added]);
      toast.success(`${added.length} immagine/i aggiunta/e`);
    }
    setBusy(false);
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  return (
    <div>
      <span className="text-xs font-bold uppercase tracking-wider text-[#7A7369]">Galleria (max 6 immagini)</span>
      <div className="mt-2 grid grid-cols-3 md:grid-cols-6 gap-3">
        {(value || []).map((url, i) => {
          const src = url.startsWith("http") ? url : `${BACKEND}${url}`;
          return (
            <div key={i} className="relative group aspect-square rounded-2xl overflow-hidden bg-[#F2EBE1]">
              <img src={src} alt="" className="w-full h-full object-cover"/>
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-[#2C312E]/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Rimuovi"
              >
                <X size={14}/>
              </button>
            </div>
          );
        })}
        {(value?.length ?? 0) < 6 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="aspect-square rounded-2xl border-2 border-dashed border-[#E5DCD0] bg-white flex flex-col items-center justify-center gap-1 hover:border-[#4A6B53] hover:bg-[#4A6B53]/5 transition-all text-[#4A6B53] disabled:opacity-60"
            data-testid={testid}
          >
            {busy ? <Loader2 size={20} className="animate-spin"/> : <><ImagePlus size={20}/><span className="text-[11px] font-bold">Aggiungi</span></>}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
    </div>
  );
}
